import type {
  AdminAttachmentTemplateBody,
  AdminAttachmentTemplateDetail,
  AdminAttachmentTemplatesResponse,
} from '@/api/(admin)/attachment-templates/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Admin attachment templates.
 *
 * These are the **modern** routes on `VITE_BACKEND_URI`
 * (`backend/src/api/routes/admin/attachment-templates/`), not the `/v3`
 * api-service ones the `(admin)/materials` and `(admin)/standards` modules use
 * — the same split as `(admin)/users`. Passing the base explicitly is what
 * keeps these from 404ing on the api-service host.
 *
 * Deliberately absent: archive. `POST /:id/archive` exists but answers 403
 * `cannot_archive_global_template` for every row this screen can see, because
 * `POST /` hard-codes `isGlobal: true`. There is no delete route at all.
 */

/** The list. Takes a pre-built query string; paging and filters live in the hook. */
export async function fetchAdminAttachmentTemplates(
  qs: string,
): Promise<AdminAttachmentTemplatesResponse> {
  return apiFetch<AdminAttachmentTemplatesResponse>(
    `/admin/attachment-templates?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** One template with its rules and `hasOrders`. Returns the `{ data }` envelope. */
export async function fetchAdminAttachmentTemplateDetails(
  id: string,
): Promise<{ data: AdminAttachmentTemplateDetail }> {
  return apiFetch<{ data: AdminAttachmentTemplateDetail }>(
    `/admin/attachment-templates/${encodeURIComponent(id)}/details`,
    {},
    BACKEND_URL,
  );
}

/** Creates one. The route forces `isGlobal: true`, so every template made here is global. */
export async function createAdminAttachmentTemplate(
  body: AdminAttachmentTemplateBody,
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    '/admin/attachment-templates',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/**
 * Updates one. A PATCH, and it replaces every rule row rather than merging.
 *
 * Answers 409 `template_in_use` when an order references the template, 422
 * `invalid_facility_process_ids` for unknown facility processes, and 404 when
 * the template is not global.
 */
export async function updateAdminAttachmentTemplate(
  id: string,
  body: AdminAttachmentTemplateBody,
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    `/admin/attachment-templates/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}
